import { authService } from '../auth/auth.service';
import endpoint from '../util/endpoint';
import { StoreRoles, Subscription, TenantRoles, User } from './user';
import type { UserTableFilter } from './userListTableColumns';
import { storeManagerEndpoints } from '@/lib/endpoints';
import { t, type useTranslator } from '@/translations/translator';
import { RequestError } from '@whiz-cart/node-shared/endpoint/error';
import { errorToString } from '@whiz-cart/node-shared/errorToString';
import { service } from '@whiz-cart/node-shared/service/service';
import { urlService } from '@whiz-cart/ui-shared/url/url.service';
import { createCache } from 'cross-state';
import { ReactNode } from 'react';
import { Action } from 'schummar-state/react';

export type ScopeType = 'store' | 'tenant' | 'global';

export class AccountControllerError extends Error {
    contextValue;
    status;
    httpStatus;

    constructor(e: any) {
        super(errorToString(e));
        const { contextValue, status } = (e?.data as { accounts: any; contextValue: string; status: number }) ?? {};
        this.cause = e;
        this.name = 'AccountControllerError';
        this.contextValue = contextValue;
        this.status = status;
        this.httpStatus = e?.response?.status;
    }

    getTranslatedMessage(t: ReturnType<typeof useTranslator>): string;
    getTranslatedMessage(): ReactNode;
    getTranslatedMessage(_t?: ReturnType<typeof useTranslator>) {
        return (_t ?? t).unknown(
            `profile.errors.${this.status}`,
            { contextValue: this.contextValue },
            { fallback: errorToString(this.cause) },
        );
    }
}

const transformRoles = ({
    globalRoles,
    storeRoles,
    tenantRoles,
    ...user
}: {
    globalRoles?: string[] | null;
    storeRoles?: StoreRoles[] | null;
    tenantRoles?: TenantRoles[] | null;
}) => {
    const roles: any = {};
    if (globalRoles && globalRoles?.length > 0) {
        roles['global'] = { scopeType: 'global', scope: 'global', roles: globalRoles };
    }
    if (storeRoles) {
        storeRoles.forEach((val: any) => {
            roles[val.storeGuid] = { scopeType: 'store', scope: val.storeGuid, roles: val.roles };
        });
    }
    if (tenantRoles) {
        tenantRoles.forEach((val: any) => {
            roles[val.tenant] = { scopeType: 'tenant', scope: val.tenant, roles: val.roles };
        });
    }
    return {
        ...user,
        scopeRoles: roles,
    } as User;
};

export default service(
    'userService',
    class UserService {
        /**
         * Migrated to new Request-Lib with AbortSignal support for race condition handling.
         * Other methods in this service still use the old endpoint() system and can be migrated as needed.
         */
        async list(
            query: string,
            skip: number,
            limit: number,
            sort: Array<{ sortBy: string; order: number }>,
            filters: UserTableFilter[],
            storeGuid?: string,
            signal?: AbortSignal,
        ) {
            try {
                const sortParams = sort.map((s) => `${s.sortBy},${s.order}`);
                let filterParams = filters?.length
                    ? filters.filter((f) => f.field !== 'storeNameQuery' && f.field !== 'roles').map((f) => `${f.field},${f.value[0]}`)
                    : [];

                const storeNameQuery = filters?.find((f) => f.field === 'storeNameQuery')?.value[0];
                const roleFilter = filters?.find((f) => f.field === 'roles');

                if (roleFilter?.value?.length) {
                    filterParams = [
                        ...filterParams,
                        `[${['globalRoles', 'tenantRoles.roles', 'storeRoles.roles'].join(', ')}],
                         [${roleFilter.value.join(', ')}]`,
                    ];
                }

                const response = await storeManagerEndpoints.user
                    .listAccounts({
                        skip,
                        limit,
                    })
                    .fetch({
                        query: {
                            query,
                            storeGuid,
                            sort: sortParams,
                            filter: filterParams,
                            storeNameQuery,
                        },
                        signal,
                    });

                const { totalItemCount, items } = response;
                return {
                    count: totalItemCount,
                    items: items.map(transformRoles),
                };
            } catch (e) {
                throw new AccountControllerError(e);
            }
        }

        user = new Action(async (accountGuid: string) => {
            try {
                const response = await endpoint('storeManager.getAccount', { accountGuid }).get();
                if (!response) {
                    return null;
                }

                const user = transformRoles(response as any);
                return User.parse(user);
            } catch (error) {
                if (error instanceof RequestError && error.response.status === 404) {
                    return null;
                }

                throw new AccountControllerError(error);
            }
        });

        accountName = createCache(async (accountGuid: string) => {
            try {
                return await storeManagerEndpoints.accounts.getAccountName({ accountGuid }).fetch();
            } catch (error) {
                if (error instanceof RequestError && error.response.status === 404) {
                    return null;
                }
                throw error;
            }
        });

        async create(user: any) {
            try {
                const response: any = await endpoint('storeManager.createAccount').post(user);
                this.user.setCache(response.accountGuid, transformRoles(response));
                return response;
            } catch (e) {
                throw new AccountControllerError(e);
            }
        }

        async updateRoles(accountGuid: string, scopeType: ScopeType, scope: string, roles: string[]) {
            try {
                switch (scopeType) {
                    case 'store':
                        return await endpoint('storeManager.updateAccountStoreRoles', { storeGuid: scope, accountGuid }).post(roles);
                    case 'tenant':
                        return await endpoint('storeManager.updateAccountTenantRoles', { tenant: scope, accountGuid }).post(roles);
                    case 'global':
                        return await endpoint('storeManager.updateAccountGlobalRoles', { accountGuid }).post(roles);
                }
            } catch (e) {
                throw new AccountControllerError(e);
            }
        }

        updateLanguage(language: string | undefined | null) {
            if (!language) {
                return;
            }
            authService.loginState.update((state) => {
                state.locale = language;
            });
        }

        async update(accountGuid: string, update: any) {
            try {
                const response: any = await endpoint('storeManager.updateAccount', { accountGuid }).post(update);
                this.user.setCache(accountGuid, transformRoles(response));
                authService.updateLanguage(response.userLanguage);
                return response;
            } catch (e: any) {
                throw new AccountControllerError(e);
            }
        }

        async delete(accountGuid: string) {
            try {
                await endpoint('storeManager.deleteAccount', { accountGuid }).delete();
                this.user.clearCache(accountGuid);
            } catch (e: any) {
                throw new AccountControllerError(e);
            }
        }

        async resetPassword(accountGuid: string, password: string) {
            try {
                await endpoint('storeManager.resetPassword').post({ accountGuid, password });
            } catch (e: any) {
                throw new AccountControllerError(e);
            }
        }

        async activate({ token, userName, password, type }: { token: string; userName: string; password: string; type: string }) {
            const claims = await authService.getLoginClaims();
            const response: any = await endpoint('storeManager.setPasswordWithEmailToken').post(
                {
                    token,
                    userName,
                    password,
                    accountEmailType: type,
                    claims,
                },
                { accessToken: false },
            );
            await authService.acceptLogin(response);
            urlService.pushUrl('/', { token: undefined, type: undefined, userName: undefined });
        }

        async forgotPassword(email: string) {
            await endpoint('storeManager.forgotPassword').post({ email }, { accessToken: false });
            urlService.pushUrl('/');
        }

        async getSubscriptions(storeGuid: string) {
            return endpoint('storeManager.getSubscriptions', { storeGuid }).get();
        }

        subscriptions = new Action(async (storeGuid?: string) => {
            if (!storeGuid) {
                return [];
            }
            const result = await endpoint('storeManager.getSubscriptions', { storeGuid }).get();
            try {
                return Subscription.array().parse(result);
            } catch (error) {
                console.error('error', error);
                return [];
            }
        });
    },
);
